import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import AccuracyChart from "../components/AccuracyChart";
import DepotSelect, { useDepot } from "../components/DepotSelect";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };
const DAY_TYPES = ["weekday", "sat", "holiday"];

/**
 * 解析の健康診断。
 *
 * 乗番の中身ではなく、**乗番が正しいかどうか**を外から見る画面。
 *
 *   精度の推移     … 毎晩 前日ぶんを測って積んだもの（duty:evaluate --store）
 *   ダイヤ改正     … 便が入れ替わった日。抽出の窓はここで切られる
 *
 * 一般公開に進めるかどうかの判断は、ここの網羅率で行う。
 */
export default function AnalysisHealthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 営業所は乗番がある営業所から（決め打ちしない）
  const [depot] = useDepot(searchParams, setSearchParams);
  const dayType = searchParams.get("day_type") || "weekday";

  const [accuracy, setAccuracy] = useState(null);
  const [revisions, setRevisions] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRevisions = useCallback(() => {
    axios
      .get(`/analysis/revisions?depot=${encodeURIComponent(depot)}&day_type=${dayType}`)
      .then((res) => setRevisions(res.data))
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"));
  }, [depot, dayType]);

  useEffect(() => {
    if (!depot) return;      // 既定の営業所を引いている最中
    setLoading(true);
    setError(null);

    Promise.all([
      axios.get(`/analysis/accuracy?depot=${encodeURIComponent(depot)}&days=60`).then((res) => setAccuracy(res.data)),
      axios
        .get(`/analysis/revisions?depot=${encodeURIComponent(depot)}&day_type=${dayType}`)
        .then((res) => setRevisions(res.data)),
    ])
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  }, [depot, dayType]);

  function dismissRevision(revision, next) {
    axios
      .put(`/analysis/revisions/${revision.id}`, { status: next })
      .then(loadRevisions)
      .catch((e) => setError(e.response?.data?.message ?? "更新に失敗しました。"));
  }

  const totals = accuracy?.totals;

  return (
    <div className="anl-page">
      <div className="anl-header">
        <button className="anl-back" onClick={() => navigate("/analysis")}>
          運用解析へ戻る
        </button>
        <h2 className="anl-title">
          解析の健康診断
          <DepotSelect depot={depot} onChange={(d) => setSearchParams({ depot: d, day_type: dayType })} />
        </h2>
        <p className="anl-subtitle">
          {depot}営業所について、予測がどれくらい当たっているかと、ダイヤが入れ替わった日を出しています。
        </p>
      </div>

      {loading && <p className="anl-empty">読み込み中…</p>}
      {error && <div className="anl-warning">{error}</div>}

      {accuracy && !loading && (
        <div className="anl-card">
          <div className="anl-card__header">
            予測の精度
            {totals?.days > 0 && (
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                直近{totals.days}運行日 · 台日{totals.bus_days}
              </span>
            )}
          </div>
          <div className="anl-card__body">
            <AccuracyChart points={accuracy.points} target={accuracy.target_coverage_rate} />

            {totals?.days > 0 && (
              <div className="anl-stats">
                <Stat
                  label="網羅率"
                  value={totals.coverage_rate}
                  note="観測された便のうち予測できた割合"
                  target={accuracy.target_coverage_rate}
                />
                <Stat label="的中率" value={totals.hit_rate} note="予測した便のうち観測された割合" />
                <Stat label="特定率" value={totals.identify_rate} note="乗番を特定できた台日の割合" />
                <div className="anl-stat">
                  <div className="anl-stat__label">取り違え</div>
                  <div className="anl-stat__value">{totals.misidentified}</div>
                  <div className="anl-stat__note">1日ぶん見て判定し直すと違った台日</div>
                </div>
              </div>
            )}

            <p className="anl-note">
              毎晩04:30に、前の運行日ぶんを12:00締切で測っています。
              <strong>乗番はその日より前のデータだけで組み直します。</strong>
              答えを見ながら答え合わせをしないためです。
            </p>
          </div>
        </div>
      )}

      {revisions && !loading && (
        <div className="anl-card">
          <div className="anl-card__header">
            ダイヤ改正
            {revisions.revisions.length > 0 && (
              <span style={{ fontWeight: 400, color: "#6b7280" }}>
                {revisions.revisions.length}件
              </span>
            )}
          </div>
          <div className="anl-card__body">
            <div className="anl-tabs">
              {DAY_TYPES.map((key) => (
                <button
                  key={key}
                  className={`anl-tab ${dayType === key ? "anl-tab--active" : ""}`}
                  onClick={() => setSearchParams({ depot, day_type: key })}
                >
                  {DAY_TYPE_LABEL[key]}
                </button>
              ))}
            </div>

            {revisions.window_start ? (
              <div className="anl-notice anl-notice--change">
                いま乗番を組むのに使っているのは <strong>{revisions.window_start}</strong> 以降の
                目撃だけです。それより前はダイヤが違うため見ていません。
              </div>
            ) : (
              <div className="anl-notice anl-notice--quiet">
                改正は見つかっていません。窓のぶん（既定90日）をそのまま使っています。
              </div>
            )}

            {revisions.revisions.length > 0 && (
              <div className="anl-table-wrap">
                <table className="anl-table">
                  <thead>
                    <tr>
                      <th>改正日</th>
                      <th>入れ替わり</th>
                      <th>前の便</th>
                      <th>後の便</th>
                      <th>増</th>
                      <th>減</th>
                      <th>状態</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {revisions.revisions.map((r) => (
                      <tr key={r.id}>
                        <td>{r.service_date}</td>
                        {r.source === "announced" ? (
                          /* 西鉄の告知から人が登録した改正。観測で比べていないので
                             便の数は無い。**0 と書かない**（「便が無い」と読まれる） */
                          <td colSpan={5} title={r.note ?? ""}>
                            告知で登録{r.note ? `（${r.note}）` : ""}
                            {!r.cuts_window && " · 窓は切らない（旧乗番を残すためだけの日付。窓は自動検出に任せる）"}
                          </td>
                        ) : (
                          <>
                            <td className="anl-table__num">
                              共通{Math.round(r.similarity * 100)}%
                            </td>
                            <td className="anl-table__num">{r.trips_before}</td>
                            <td className="anl-table__num">{r.trips_after}</td>
                            <td className="anl-table__num">+{r.trips_added}</td>
                            <td className="anl-table__num">-{r.trips_removed}</td>
                          </>
                        )}
                        <td>{r.status === "confirmed" ? "改正として扱う" : "改正ではない"}</td>
                        <td>
                          <button
                            className="anl-link"
                            onClick={() =>
                              dismissRevision(r, r.status === "confirmed" ? "dismissed" : "confirmed")
                            }
                          >
                            {r.status === "confirmed" ? "改正ではない" : "改正として扱う"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <SimilaritySeries series={revisions.series} threshold={revisions.min_similarity} />

            <p className="anl-note">
              手前{revisions.side_days}運行日と、その日からの{revisions.side_days}運行日で、
              走った便がどれだけ共通しているかを見ています。
              <strong>検出は{revisions.side_days}日ほど遅れます。</strong>
              改正の当日には出ません。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, note, target }) {
  const reached = target !== undefined && value !== null && value >= target;

  return (
    <div className="anl-stat">
      <div className="anl-stat__label">{label}</div>
      <div className="anl-stat__value">
        {value === null || value === undefined ? "—" : `${Math.round(value * 1000) / 10}%`}
        {target !== undefined && value !== null && (
          <span className={`anl-badge ${reached ? "anl-badge--high" : "anl-badge--mid"}`}>
            {reached ? "目標到達" : `目標${Math.round(target * 100)}%`}
          </span>
        )}
      </div>
      <div className="anl-stat__note">{note}</div>
    </div>
  );
}

/**
 * 運行日ごとの入れ替わりぐあい。
 *
 * **比べられなかった日を 0 として描かない。**前後の運行日が足りない日は
 * 帯を空けておく。0 で埋めると「まったく違う日」に見える。
 */
function SimilaritySeries({ series, threshold }) {
  const compared = series.filter((p) => p.similarity !== null);

  if (compared.length === 0) {
    return (
      <p className="anl-empty">
        まだ比べられていません。前後にそれぞれ数日ぶんの濃い運行日が要ります。
      </p>
    );
  }

  return (
    <div className="anl-similarity">
      {series.map((p) => (
        <div
          key={p.service_date}
          className={`anl-similarity__cell ${
            p.similarity === null
              ? "anl-similarity__cell--unknown"
              : p.similarity < threshold
                ? "anl-similarity__cell--low"
                : ""
          }`}
          title={
            p.similarity === null
              ? `${p.service_date}: 比べられません（前後の運行日が足りない）`
              : `${p.service_date}: 共通${Math.round(p.similarity * 100)}% / 増${p.added}減${p.removed}`
          }
          style={
            p.similarity === null
              ? undefined
              : { opacity: 0.25 + 0.75 * (1 - p.similarity) }
          }
        />
      ))}
    </div>
  );
}
